// src/services/employeeTrackingService.ts
import api from './api';

interface PerformanceData {
  id: number;
  user_id: number;
  employee_name: string;
  employee_email: string;
  date: string;
  items_received: number;
  orders_shipped: number;
  items_shipped: number;
  updated_at: string;
}

interface ActionData {
  id: number;
  user_id: number;
  employee_name: string;
  employee_email: string;
  action_type: string;
  order_id: string;
  item_id: string;
  quantity: number;
  action_timestamp: string;
  additional_data: any;
  created_at: string;
}

interface EmployeeSummary {
  user_id: number;
  employee_name: string;
  items_received: number;
  orders_shipped: number;
  items_shipped: number;
  efficiency: number; // Items per day
}

interface ComparisonResponse {
  employees: EmployeeSummary[];
  dateRange: {
    startDate: string;
    endDate: string;
    days: number;
  };
}

// Tracking cache to prevent duplicate API calls
interface TrackingCache {
  [key: string]: {
    timestamp: number;
    result: boolean;
  }
}

class EmployeeTrackingService {
  private trackingCache: TrackingCache = {};
  private cacheExpirationMs: number = 60 * 60 * 1000; // 1 hour

  // Helper function to prevent duplicate tracking
  private async trackWithDedupe(
    cacheKey: string, 
    trackingFn: () => Promise<boolean>
  ): Promise<boolean> {
    try {
      // Check cache first
      const now = Date.now();
      const cachedItem = this.trackingCache[cacheKey];
      
      // If we have a valid cached result, return it
      if (cachedItem && (now - cachedItem.timestamp) < this.cacheExpirationMs) {
        console.log(`Using cached result for tracking key: ${cacheKey}`);
        return cachedItem.result;
      }
      
      // Not in cache or expired, execute the tracking function
      const result = await trackingFn();
      
      // Cache the result for future calls
      this.trackingCache[cacheKey] = {
        timestamp: now,
        result
      };
      
      return result;
    } catch (error) {
      console.error(`Error in trackWithDedupe for key ${cacheKey}:`, error);
      return false;
    }
  }

  // Track when an employee marks an item as received
  async trackItemReceived(orderId: string, itemId: string, quantity: number = 1, additionalData?: any) {
    // Create a unique cache key for this action
    const userId = additionalData?.user_id || localStorage.getItem('userId');
    const date = new Date().toISOString().split('T')[0]; // Current date YYYY-MM-DD
    const cacheKey = `received_${userId}_${orderId}_${itemId}_${date}`;
    
    return this.trackWithDedupe(cacheKey, async () => {
      try {
        // Log that we're making a real API call
        console.log('Making API call to track item received:', {
          orderId,
          itemId,
          userId,
          date
        });
        
        await api.post('/api/employee/actions', {
          user_id: userId,
          action_type: 'ITEM_RECEIVED',
          order_id: orderId,
          item_id: itemId,
          quantity,
          additional_data: additionalData
        });
        return true;
      } catch (error) {
        console.error('Failed to track item received:', error);
        return false;
      }
    });
  }

  // Track when an employee ships an order
  async trackOrderShipped(orderId: string, itemCount: number, additionalData?: any) {
    // Create a unique cache key for this action
    const userId = additionalData?.user_id || localStorage.getItem('userId');
    const date = new Date().toISOString().split('T')[0]; // Current date YYYY-MM-DD
    const cacheKey = `shipped_${userId}_${orderId}_${date}`;
    
    return this.trackWithDedupe(cacheKey, async () => {
      try {
        console.log('Making API call to track order shipped:', {
          orderId,
          userId,
          date
        });
        
        await api.post('/api/employee/actions', {
          user_id: userId,
          action_type: 'ORDER_SHIPPED',
          order_id: orderId,
          quantity: itemCount,
          additional_data: additionalData
        });
        return true;
      } catch (error) {
        console.error('Failed to track order shipped:', error);
        return false;
      }
    });
  }

  // Track when an employee prints a shipping document
  async trackShippingDocumentPrinted(orderId: string, additionalData?: any) {
    // Create a unique cache key for this action
    const userId = additionalData?.user_id || localStorage.getItem('userId');
    const date = new Date().toISOString().split('T')[0]; // Current date YYYY-MM-DD
    const cacheKey = `printed_${userId}_${orderId}_${date}`;
    
    return this.trackWithDedupe(cacheKey, async () => {
      try {
        await api.post('/api/employee/actions', {
          user_id: userId,
          action_type: 'SHIPPING_DOC_PRINTED',
          order_id: orderId,
          additional_data: additionalData
        });
        return true;
      } catch (error) {
        console.error('Failed to track shipping document printed:', error);
        return false;
      }
    });
  }

  // Get current employee's performance
  async getMyPerformance(startDate?: string, endDate?: string) {
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const userId = localStorage.getItem('userId');
      if (userId) params.userId = userId;
      
      const response = await api.get('/api/employee/performance', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      throw error;
    }
  }

  // Get detailed action logs for the current employee
  async getMyActionLogs(startDate?: string, endDate?: string, actionType?: string, limit: number = 100) {
    try {
      const params: Record<string, string | number> = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (actionType && actionType !== 'all') params.actionType = actionType;
      
      const userId = localStorage.getItem('userId');
      if (userId) params.userId = userId;
      
      const response = await api.get('/api/employee/actions', { params });
      return response.data.actions;
    } catch (error) {
      console.error('Failed to fetch action logs:', error);
      throw error;
    }
  }

  // Get comparison data for all employees
  async getEmployeeComparison(startDate?: string, endDate?: string): Promise<ComparisonResponse> {
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await api.get('/api/employee/performance/compare', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch employee comparison data:', error);
      throw error;
    }
  }
}

export default new EmployeeTrackingService();